import {
    GoogleAuthProvider,
    signInWithEmailAndPassword,
    signInWithPopup,
} from "firebase/auth";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FirebaseError } from "firebase/app";
import * as Sentry from "@sentry/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Input } from "@jhool-io/fe-components";
import AuthWrapper from "../components/AuthWrapper/AuthWrapper";
import GoogleIcon from "../../../components/Icons/GoogleIcon";
import useToast from "../../../hooks/useToast";
import { auth } from "../../../firebase";
import { syncGoogleName } from "../../../services/user";

interface ILoginProps {
    email: string;
    password: string;
}

const schema = yup.object({
    email: yup
        .string()
        .email("Please enter a valid email")
        .required("Email is required"),
    password: yup.string().required("Password is required"),
});

export default function LoginPage() {
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<ILoginProps>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const { toast } = useToast();

    const handleErrorMessageToShow = (errorCode: string) => {
        if (errorCode === "auth/wrong-password") {
            return "Your email or password is incorrect.";
        }
        if (errorCode === "auth/user-not-found") {
            return "We couldn't find an account with that email. Please check your email.";
        }
        return "Could not log you in at this time, please try again later";
    };

    const redirectUrl = searchParams.get("redirectUrl");

    const getSafeRedirectPath = () => {
        const raw = redirectUrl?.replaceAll('"', "") ?? "/";

        if (!raw.startsWith("/")) {
            return "/";
        }

        if (raw.startsWith("//")) {
            return "/";
        }

        if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(raw)) {
            return "/";
        }

        return raw;
    };

    const onSubmit = async (data: ILoginProps) => {
        try {
            await signInWithEmailAndPassword(auth, data.email, data.password);

            navigate(getSafeRedirectPath(), {
                replace: true,
            });
        } catch (error) {
            if (error instanceof FirebaseError) {
                Sentry.logger.error("Email/password login failed", {
                    error_code: error.code,
                });
                toast({
                    mode: "error",
                    message: handleErrorMessageToShow(error.code),
                });
            } else {
                toast({
                    mode: "error",
                    message:
                        "Could not log you in at this time, please try again later",
                });
            }
        }
    };

    async function signinWithGoogle() {
        try {
            await signInWithPopup(auth, new GoogleAuthProvider());
            await syncGoogleName().catch((error: unknown) => {
                Sentry.logger.error("Google name sync failed", {
                    error_message:
                        error instanceof Error
                            ? error.message
                            : "Unknown name sync error",
                });
            });

            navigate(getSafeRedirectPath(), {
                replace: true,
            });
        } catch (error) {
            if (error instanceof FirebaseError) {
                Sentry.logger.error("Google login failed", {
                    error_code: error.code,
                    provider: "google",
                });
                toast({
                    mode: "error",
                    message: handleErrorMessageToShow(error.code),
                });
            } else {
                Sentry.logger.error("Google login failed", {
                    error_message:
                        error instanceof Error
                            ? error.message
                            : "Unknown login error",
                    provider: "google",
                });
                toast({
                    mode: "error",
                    message:
                        "Could not log you in at this time, please try again later",
                });
            }
        }
    }

    return (
        <AuthWrapper
            footer={
                <div className="text-center">
                    <Link
                        to="/reset-password"
                        className="link text-base tracking-[-0.154px]"
                    >
                        Reset password?
                    </Link>
                </div>
            }
        >
            <>
                <h3 className="text-titleLg font-bold text-text mb-[--spacing-items]">
                    Welcome back
                </h3>
                <Button
                    size="medium"
                    variant="secondary"
                    className="w-full"
                    onClick={() => signinWithGoogle()}
                    disabled={isSubmitting}
                    iconLeft={<GoogleIcon />}
                >
                    Continue with Google
                </Button>
                <div
                    className={[
                        "flex items-center justify-between",
                        "before:content-[''] before:w-[45%] before:h-[1px]",
                        "before:bg-stroke after:content-[''] after:w-[45%]",
                        "after:h-[1px] after:bg-stroke my-[--spacing-items]",
                    ].join(" ")}
                >
                    OR
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-[--spacing-form-items]">
                        <Input
                            {...register("email")}
                            label="Email Address"
                            placeholder="Enter your email address"
                            hasError={!!errors.email}
                            errorText={errors.email?.message}
                            inputSize="medium"
                        />
                    </div>
                    <div className="mb-[--spacing-form-items]">
                        <Input
                            {...register("password")}
                            label="Password"
                            type="password"
                            placeholder="Enter your password"
                            hasError={!!errors.password}
                            errorText={errors.password?.message}
                            inputSize="medium"
                        />
                    </div>
                    <Button
                        disabled={isSubmitting}
                        type="submit"
                        className="w-full"
                        size="medium"
                    >
                        Login
                    </Button>
                </form>
            </>
        </AuthWrapper>
    );
}
